import './Viewer.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import useSize from '@react-hook/size';
import { useSpring, animated } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';

import { classNames as cn } from '$utils';
import { ViewerProps } from './Viewer.interface';
import { ViewerItem } from './VieverItem/ViewerItem';
import { ZoomValues } from './VieverItem/ViewerItem.interface';
import { Header } from '$uikit';
import { IcAngleLeft28, IcAngleRight28 } from '$assets';
import { Loader } from '$shared/components';
import {
  clamp,
  frictionScale,
  getFitSize,
  getPanBounds,
  getZoomLevels,
  project,
  rubberband,
  zoomAtPoint,
} from './zoom';

const HEADER_HEIGHT = 56;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_RADIUS = 40;
const WHEEL_SWIPE_X = 100;
const WHEEL_SWIPE_Y = 150;
const WHEEL_AXIS_THRESHOLD = 10;
const WHEEL_SESSION_GAP = 300;
const ZOOM_CONFIG = { tension: 350, friction: 36, clamp: true };
const PAN_CONFIG = { tension: 280, friction: 30 };

interface Point {
  x: number;
  y: number;
}

export const Viewer: FC<ViewerProps> = ({
  items,
  active = 0,
  onClose,
  onChange,
  title,
  mobile = true,
}) => {
  const target = useRef<HTMLDivElement>(null);
  const [viewWidth, viewHeight] = useSize(target);
  const [skipAnim, setSkipAnim] = useState(true);
  const [isZoomed, setZoomed] = useState(false);
  const [isControlsShown, setControlsShown] = useState(true);
  const [activeArrow, setActiveArrow] = useState(0);
  const zoomedRef = useRef(false);
  const zoomTarget = useRef<ZoomValues>({ x: 0, y: 0, scale: 1 });
  const tapRef = useRef({ x: 0, y: 0, time: 0, timer: 0 });
  const wheelRef = useRef({ axis: 0, acc: [0, 0], fired: false, last: 0, timer: 0 });
  const [{ offsetX, offsetY, bgOpacity, headerOpacity }, api] = useSpring(() => ({
    offsetX: -viewWidth * active,
    offsetY: 0,
    headerOpacity: isControlsShown ? 1 : 0,
    bgOpacity: 1,
    immediate: true,
    config: {
      tension: 350,
      clamp: true,
    },
  }));
  const [zoom, zoomApi] = useSpring(() => ({
    x: 0,
    y: 0,
    scale: 1,
    config: ZOOM_CONFIG,
  }));

  const item = items[active];
  const isPhoto = !!item && item.id > 0;
  const fit = item
    ? getFitSize(item, viewWidth, viewHeight, mobile)
    : { width: 0, height: 0 };
  const levels = getZoomLevels(item, fit);

  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      e.stopPropagation();
      e.key === 'Escape' && onClose && onClose();
    };

    window.addEventListener('keydown', escape, false);

    return () => {
      window.removeEventListener('keydown', escape, false);
    };
  }, [onClose]);

  useEffect(() => {
    const handleGesture = (e) => e.preventDefault();
    document.addEventListener('gesturestart', handleGesture);
    return () => document.removeEventListener('gesturestart', handleGesture);
  }, []);

  // no native scroll / page zoom while the viewer is open (iOS ignores user-scalable=no)
  useEffect(() => {
    const el = target.current;
    const prevent = (e: TouchEvent) => e.preventDefault();
    el?.addEventListener('touchmove', prevent, { passive: false });
    return () => el?.removeEventListener('touchmove', prevent);
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(tapRef.current.timer);
      window.clearTimeout(wheelRef.current.timer);
    },
    [],
  );

  const calculateTitle = items.length
    ? title || `${active + 1}/${items.length}`
    : 'Loading...';

  const getDirectionByPointer = useCallback(
    (clientX) => {
      const btnWidth = items.length > 1 ? (mobile ? 60 : viewWidth / 2) : 0;
      if (clientX > viewWidth - btnWidth) {
        return 1;
      } else if (clientX < btnWidth) {
        return -1;
      } else {
        return 0;
      }
    },
    [mobile, viewWidth, items],
  );

  const prev = useCallback(
    (loop) => onChange(active === 0 ? (loop ? items.length - 1 : active) : active - 1),
    [active, items, onClose],
  );

  const next = useCallback(
    (loop) => onChange(active === items.length - 1 ? (loop ? 0 : active) : active + 1),
    [active, items, onClose],
  );

  const setZoomedState = (value: boolean) => {
    if (zoomedRef.current !== value) {
      zoomedRef.current = value;
      setZoomed(value);
    }
  };

  const getBounds = (scale: number) => getPanBounds(fit, scale, viewWidth, viewHeight);

  const toPoint = (clientX: number, clientY: number): Point => {
    const rect = target.current?.getBoundingClientRect();
    return {
      x: clientX - (rect?.left || 0) - viewWidth / 2,
      y: clientY - (rect?.top || 0) - viewHeight / 2,
    };
  };

  const applyZoom = (next: Partial<ZoomValues>, config?: typeof ZOOM_CONFIG) => {
    Object.assign(zoomTarget.current, next);
    if (config) {
      zoomApi.start({ ...next, config });
    } else {
      zoomApi.set(next);
    }
    setZoomedState(zoomTarget.current.scale > 1);
  };

  const zoomTo = (scale: number, focal: Point) => {
    if (!isPhoto) {
      return;
    }
    const { x: x0, y: y0, scale: s0 } = zoomTarget.current;
    const s1 = clamp(scale, 1, levels.max);
    let { x, y } = zoomAtPoint(focal.x, focal.y, x0, y0, s0, s1);
    if (s1 <= 1) {
      x = 0;
      y = 0;
    } else {
      const bounds = getBounds(s1);
      x = clamp(x, -bounds.x, bounds.x);
      y = clamp(y, -bounds.y, bounds.y);
    }
    applyZoom({ x, y, scale: s1 }, ZOOM_CONFIG);
  };

  const toggleZoom = (focal: Point) =>
    zoomTo(zoomTarget.current.scale > 1 ? 1 : levels.doubleTap, focal);

  const finishPan = (vx: number, vy: number) => {
    const { x, y, scale } = zoomTarget.current;
    const bounds = getBounds(scale);
    const next = {
      x: clamp(x + project(vx), -bounds.x, bounds.x),
      y: clamp(y + project(vy), -bounds.y, bounds.y),
    };
    Object.assign(zoomTarget.current, next);
    zoomApi.start({ x: next.x, config: { ...PAN_CONFIG, velocity: vx } });
    zoomApi.start({ y: next.y, config: { ...PAN_CONFIG, velocity: vy } });
  };

  const closeBySwipe = (direction: number) =>
    api.start({
      headerOpacity: 0,
      bgOpacity: 0,
      offsetY: viewHeight * 1.2 * direction,
      onRest: ({ finished }) => {
        finished && onClose && onClose();
      },
    });

  const handleTap = (clientX: number, clientY: number, time: number) => {
    if (clientY <= HEADER_HEIGHT) {
      return;
    }
    const tap = tapRef.current;
    if (
      tap.timer &&
      time - tap.time < DOUBLE_TAP_DELAY &&
      Math.hypot(clientX - tap.x, clientY - tap.y) < DOUBLE_TAP_RADIUS
    ) {
      window.clearTimeout(tap.timer);
      tap.timer = 0;
      toggleZoom(toPoint(clientX, clientY));
      return;
    }
    const direction = zoomedRef.current ? 0 : getDirectionByPointer(clientX);
    if (direction) {
      setSkipAnim(true);
      direction > 0 ? next(true) : prev(true);
      return;
    }
    tap.x = clientX;
    tap.y = clientY;
    tap.time = time;
    tap.timer = window.setTimeout(() => {
      tap.timer = 0;
      setControlsShown((shown) => !shown);
    }, DOUBLE_TAP_DELAY);
  };

  const endWheelSwipe = () => {
    const ws = wheelRef.current;
    const axis = ws.axis;
    ws.axis = 0;
    if (ws.fired || !axis) {
      return;
    }
    api.start({
      offsetX: -viewWidth * active,
      offsetY: 0,
      bgOpacity: 1,
      headerOpacity: isControlsShown ? 1 : 0,
    });
  };

  const wheelSwipe = (dx: number, dy: number, now: number) => {
    const ws = wheelRef.current;
    if (now - ws.last > WHEEL_SESSION_GAP) {
      ws.axis = 0;
      ws.acc = [0, 0];
      ws.fired = false;
    }
    ws.last = now;
    window.clearTimeout(ws.timer);
    ws.timer = window.setTimeout(endWheelSwipe, WHEEL_SESSION_GAP);
    if (ws.fired) {
      return;
    }
    ws.acc[0] += dx;
    ws.acc[1] += dy;
    if (!ws.axis) {
      const [ax, ay] = [Math.abs(ws.acc[0]), Math.abs(ws.acc[1])];
      if (ax < WHEEL_AXIS_THRESHOLD && ay < WHEEL_AXIS_THRESHOLD) {
        return;
      }
      ws.axis = ax > ay ? 1 : 2;
    }
    if (ws.axis === 1) {
      const nextIndex = active + (ws.acc[0] > 0 ? 1 : -1);
      const canMove = nextIndex >= 0 && nextIndex < items.length;
      api.start({
        offsetX: -viewWidth * active - ws.acc[0] / (canMove ? 1 : 5),
        immediate: true,
      });
      if (canMove && Math.abs(ws.acc[0]) > WHEEL_SWIPE_X) {
        ws.fired = true;
        onChange(nextIndex);
      }
    } else {
      const y = -ws.acc[1];
      const opacity = 1 - Math.abs(y) / viewHeight;
      api.start({
        offsetY: y,
        bgOpacity: opacity,
        headerOpacity: isControlsShown ? opacity : 0,
        immediate: true,
      });
      if (Math.abs(y) > WHEEL_SWIPE_Y) {
        ws.fired = true;
        closeBySwipe(y > 0 ? 1 : -1);
      }
    }
  };

  useGesture(
    {
      onDrag: (state) => {
        const {
          pinching,
          cancel,
          tap,
          down,
          memo,
          xy,
          movement: [mx, my],
          velocity: [vx, vy],
          direction: [dx, dy],
          timeStamp,
          startTime,
        } = state;
        if (pinching) {
          cancel();
          return;
        }
        if (tap) {
          mobile && handleTap(xy[0], xy[1], timeStamp);
          return;
        }
        setActiveArrow(0);

        if (zoomedRef.current && isPhoto) {
          const start = memo || { x: zoom.x.get(), y: zoom.y.get() };
          if (down) {
            const bounds = getBounds(zoomTarget.current.scale);
            applyZoom({
              x: rubberband(start.x + mx, -bounds.x, bounds.x),
              y: rubberband(start.y + my, -bounds.y, bounds.y),
            });
          } else {
            finishPan(vx * dx, vy * dy);
          }
          return start;
        }

        const currentOffset = -viewWidth * active;
        if (Math.abs(my) > Math.abs(mx)) {
          if (!down) {
            const speed = my / (timeStamp - startTime);
            if (Math.abs(speed) > 0.4 || Math.abs(my) > viewHeight / 5) {
              closeBySwipe(my > 0 ? 1 : -1);
            } else {
              api.start({
                bgOpacity: 1,
                headerOpacity: isControlsShown ? 1 : 0,
                offsetY: 0,
              });
            }
          } else {
            const opacity = 1 - Math.abs(my) / viewHeight;
            api.start({
              offsetY: my,
              bgOpacity: opacity,
              headerOpacity: isControlsShown ? opacity : 0,
              immediate: true,
            });
          }
          return;
        }

        if (!down) {
          const speed = mx / (timeStamp - startTime);
          let nextItem = 0;
          if (Math.abs(speed) > 0.4) {
            nextItem = speed > 0 ? -1 : 1;
          } else if (Math.abs(mx) > viewWidth / 3) {
            nextItem = mx > 0 ? -1 : 1;
          }
          const newActive = active + nextItem;
          if (newActive > items.length - 1 || newActive < 0 || newActive === active) {
            api.start({ offsetX: currentOffset });
          } else {
            onChange(newActive);
          }
        } else {
          const isEdge =
            (active === 0 && mx > 0) || (active === items.length - 1 && mx < 0);
          api.start({ offsetX: currentOffset + mx / (isEdge ? 5 : 1), immediate: true });
        }
      },
      onPinch: ({ event, last, origin: [ox, oy], offset: [scale], memo }) => {
        if (!isPhoto) {
          return;
        }
        if (event.cancelable && !/^(touch|pointer)/.test(event.type)) {
          event.preventDefault();
        }
        const focal = toPoint(ox, oy);
        const start = memo || { ...zoomTarget.current, fx: focal.x, fy: focal.y };
        const s1 = frictionScale(scale, 1, levels.max);
        const p = zoomAtPoint(start.fx, start.fy, start.x, start.y, start.scale, s1);
        applyZoom({
          x: p.x + focal.x - start.fx,
          y: p.y + focal.y - start.fy,
          scale: s1,
        });
        if (last) {
          zoomTo(s1, focal);
        }
        return start;
      },
      onWheel: ({ event, last, delta: [dx, dy] }) => {
        if (event.cancelable) {
          event.preventDefault();
        }
        if (last) {
          return;
        }
        // no wheel zoom; a trackpad pinch in Chrome arrives as ctrl+wheel and must not swipe
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          return;
        }
        if (zoomedRef.current) {
          const { x, y, scale } = zoomTarget.current;
          const bounds = getBounds(scale);
          applyZoom({
            x: clamp(x - dx, -bounds.x, bounds.x),
            y: clamp(y - dy, -bounds.y, bounds.y),
          });
          return;
        }
        wheelSwipe(dx, dy, event.timeStamp);
      },
      onPointerDown: ({ event }) => {
        if (
          event.clientY <= HEADER_HEIGHT ||
          (event.target as HTMLElement)?.tagName === 'VIDEO' ||
          event.button !== 0 ||
          zoomedRef.current
        ) {
          return;
        }
        const direction = getDirectionByPointer(event.clientX);

        if (Math.abs(direction)) {
          setActiveArrow(direction);
          if (!mobile) {
            direction > 0 ? next(true) : prev(true);
          }
        }
      },
      onMouseMove: ({ event }) => {
        !mobile &&
          setActiveArrow(zoomedRef.current ? 0 : getDirectionByPointer(event.clientX));
      },
    },
    {
      target,
      eventOptions: { passive: false },
      drag: {
        axis: isZoomed ? undefined : 'lock',
        filterTaps: true,
        enabled: mobile || isZoomed,
      },
      pinch: {
        from: () => [zoomTarget.current.scale, 0],
        // @use-gesture 10.2 zooms on ctrl+wheel and cannot switch it off: bind it to a key nobody holds
        modifierKey: 'altKey',
      },
    },
  );

  useEffect(() => {
    const handleUserKeyPress = (e) => {
      e.stopPropagation();
      e.preventDefault();

      const { key } = e;
      if (key === 'ArrowLeft') {
        prev(true);
      } else if (key === 'ArrowRight') {
        next(true);
      }
    };

    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [active, items, prev, next]);

  useEffect(() => {
    api.start({
      headerOpacity: isControlsShown ? 1 : 0,
    });
  }, [api, isControlsShown]);

  useEffect(() => {
    if (viewWidth) {
      setSkipAnim(false);
    }
    api.start({
      offsetX: -viewWidth * active,
      immediate: !mobile || !viewWidth || skipAnim,
      onRest: () => {
        setSkipAnim(false);
      },
    });
  }, [viewWidth, active, mobile, skipAnim]);

  useEffect(() => {
    zoomTarget.current = { x: 0, y: 0, scale: 1 };
    zoomApi.set({ x: 0, y: 0, scale: 1 });
    setZoomedState(false);
  }, [active, item?.id, viewWidth, viewHeight]);

  return (
    <div
      ref={target}
      onClick={(e) => e.stopPropagation()}
      className={cn('Viewer', {
        hideControls: !isControlsShown,
        zoomed: isZoomed,
      })}
    >
      <animated.div className="Viewer__headerWrapper" style={{ opacity: headerOpacity }}>
        <Header
          wide
          showCloseButton
          className={cn('Viewer__header', {
            visible: !!viewWidth,
          })}
          backButtonOnClick={onClose}
        >
          {calculateTitle}
        </Header>
      </animated.div>
      <animated.div className="Viewer__background" style={{ opacity: bgOpacity }} />
      {!items.length && <Loader />}
      <animated.div className="Viewer__items" style={{ x: offsetX, y: offsetY }}>
        {!!viewWidth &&
          items.map((entry, key) => {
            return key <= active + 1 && key >= active - 1 ? (
              <ViewerItem
                active={active === key}
                mobile={mobile}
                viewWidth={viewWidth}
                viewHeight={viewHeight}
                zoom={active === key ? zoom : undefined}
                key={[entry.id, entry.instance, entry.part].join('-')}
                item={entry}
              />
            ) : (
              <div
                key={['empty', entry.id, entry.instance, entry.part].join('-')}
                className="ViewerItem"
                style={{ width: viewWidth, height: viewHeight }}
              />
            );
          })}
      </animated.div>

      {items.length > 1 && (
        <>
          <div className={cn('Viewer__arrow', 'prev', { active: activeArrow === -1 })}>
            <IcAngleLeft28 />
          </div>
          <div className={cn('Viewer__arrow', 'next', { active: activeArrow === 1 })}>
            <IcAngleRight28 />
          </div>
        </>
      )}
    </div>
  );
};
